import {
	ClockIcon,
	MonitorCellphoneStarIcon,
	TextBoxIcon,
} from '@/shared/dynamicImports';
import {
	colorAccent,
	colorBackground,
	colorBackgroundBody,
	colorBlue,
	colorBorder,
	colorGray,
	colorGreen,
	colorHover,
	colorText,
	colorViolet,
	colorYellow,
} from '@/utils/palette';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'react-bootstrap';
import styled from 'styled-components';

const GpuOneLogDetailModal = ({ isOpen, onClose, log }) => {
	const [logContent, setLogContent] = useState('');
	const logContainerRef = useRef(null);
	console.log(log);

	useEffect(() => {
		if (isOpen && log) {
			// Use the existing log message as the content
			setLogContent(log.log_message || '');
		}
	}, [isOpen, log]);

	const formatLogContent = content => {
		if (!content) return '';

		// Split content into lines and format each line
		return content
			.split('\n')
			.map(line => {
				let highlightedLine = line;

				// Highlight important keywords and patterns
				highlightedLine = highlightedLine
					// Error and status indicators
					.replace(
						/(ERROR|FAILED|FAILURE|FAIL)/gi,
						'<span class="error">$1</span>',
					)
					.replace(
						/(SUCCESS|COMPLETED|OK|happy)/gi,
						'<span class="success">$1</span>',
					)
					.replace(/(WARNING|WARN)/gi, '<span class="warning">$1</span>')
					.replace(/(INFO|DEBUG)/gi, '<span class="info">$1</span>')

					// Commands and actions
					.replace(
						/(curtin:|start:|finish:|Running command|TIMED|devsync)/gi,
						'<span class="command">$1</span>',
					)
					.replace(
						/(get_path_to_storage_volume|block\.lookup_disk|Processing serial|lookup_disks found)/gi,
						'<span class="function">$1</span>',
					)

					// Device paths and hardware
					.replace(/(\/dev\/[a-zA-Z0-9]+)/g, '<span class="device">$1</span>')
					.replace(
						/(nvme[0-9]+n[0-9]+|sda|sdb)/gi,
						'<span class="device">$1</span>',
					)
					.replace(/(md[0-9]+)/gi, '<span class="raid">$1</span>')

					// Serial numbers and identifiers
					.replace(/([A-F0-9]{12,})/g, '<span class="serial">$1</span>')
					.replace(/([A-Z0-9]{8,})/g, '<span class="identifier">$1</span>')

					// Timestamps and durations
					.replace(/(\d+\.\d+)/g, '<span class="duration">$1</span>')
					.replace(
						/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/g,
						'<span class="timestamp">$1</span>',
					)

					// JSON-like structures
					.replace(/(\{[^}]*\})/g, '<span class="json">$1</span>')
					.replace(/(\[[^\]]*\])/g, '<span class="array">$1</span>')

					// File paths and commands
					.replace(/(['"][^'"]*['"])/g, '<span class="string">$1</span>')
					.replace(
						/([a-zA-Z_][a-zA-Z0-9_]*\()/g,
						'<span class="function">$1</span>',
					)

					// Numbers and sizes
					.replace(/(\d+[KMG]i?B)/gi, '<span class="size">$1</span>')
					.replace(/(\d+ blocks)/gi, '<span class="size">$1</span>')

					// Status messages
					.replace(
						/(is multipath device\?|is multipath device member\?)/gi,
						'<span class="status">$1</span>',
					)
					.replace(
						/(returning path|now exists|return volume path)/gi,
						'<span class="status">$1</span>',
					)

					// RAID and storage specific
					.replace(
						/(raid0|raid1|raid5|raid6|raid10)/gi,
						'<span class="raid">$1</span>',
					)
					.replace(
						/(active sync|active|clean|failed)/gi,
						'<span class="raid-status">$1</span>',
					)

					// Udev and system commands
					.replace(
						/(udevadm|partprobe|mdadm|wipefs|blkid)/gi,
						'<span class="system-command">$1</span>',
					)

					// Model names
					.replace(
						/(Micron_[^_]+|SAMSUNG [A-Z0-9]+)/gi,
						'<span class="model">$1</span>',
					)

					// UUIDs
					.replace(
						/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/gi,
						'<span class="uuid">$1</span>',
					);

				return `<div class="log-line">${highlightedLine}</div>`;
			})
			.join('');
	};

	const renderLogContent = () => {
		if (!logContent) return null;

		const formattedContent = formatLogContent(logContent);

		// Simply parse the HTML string directly - html-react-parser handles this elegantly
		return parse(formattedContent);
	};

	return (
		<StyledModal show={isOpen} onHide={onClose} size="xl" height="90vh">
			<ModalHeader>
				<HeaderContainer>
					<HeaderTop>
						<ModalTitle>Log Details</ModalTitle>
						<CloseButton onClick={onClose}>
							<CloseIcon>×</CloseIcon>
						</CloseButton>
					</HeaderTop>
					<HeaderInfo>
						<InfoCard>
							<InfoIcon>
								<TextBoxIcon size={20} />
							</InfoIcon>
							<InfoContent>
								<InfoLabel>Log ID</InfoLabel>
								<InfoValue>{log?.log_id}</InfoValue>
							</InfoContent>
						</InfoCard>
						<InfoCard>
							<InfoIcon>
								<MonitorCellphoneStarIcon size={20} />
							</InfoIcon>
							<InfoContent>
								<InfoLabel>Machine ID</InfoLabel>
								<InfoValue>{log?.log_machine_id}</InfoValue>
							</InfoContent>
						</InfoCard>
						<InfoCard>
							<InfoIcon>
								<ClockIcon size={20} />
							</InfoIcon>
							<InfoContent>
								<InfoLabel>Created</InfoLabel>
								<InfoValue>
									{dayjs(log?.log_created).format('MM/DD/YYYY hh:mm:ss A')}
								</InfoValue>
							</InfoContent>
						</InfoCard>
					</HeaderInfo>
				</HeaderContainer>
			</ModalHeader>
			<ModalBody>
				<LogContainer>
					<LogContent ref={logContainerRef}>{renderLogContent()}</LogContent>
					{!logContent && (
						<EmptyIndicator>No log content available</EmptyIndicator>
					)}
				</LogContainer>
			</ModalBody>
		</StyledModal>
	);
};

export default GpuOneLogDetailModal;

const StyledModal = styled(Modal)`
	.modal-content {
		height: ${props => props.height || '90vh'};
		background-color: ${colorBackground};
		border: 1px solid ${colorBorder};
		border-radius: 8px;
	}

	.modal-header {
		background-color: ${colorBackground};
		border-bottom: 1px solid ${colorBorder};
		padding: 1rem 1.5rem;
		width: 100%;
	}

	.modal-body {
		height: ${props => props.height || '90vh'};
		background-color: ${colorBackground};
		padding: 1.5rem;
	}

	.btn-close {
		color: ${colorText};
	}

	.modal-title {
		color: ${colorText};
		font-weight: 600;
	}
`;

const LogContainer = styled.div`
	position: relative;
	height: calc(100vh - 200px);
	display: flex;
	flex-direction: column;
`;

const LogContent = styled.div`
	flex: 1;
	overflow-y: auto;
	background: ${colorBackgroundBody};
	border: 1px solid ${colorBorder};
	border-radius: 8px;
	padding: 16px;
	font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
	font-size: 12px;
	line-height: 1.4;
	color: ${colorText};
	white-space: pre-wrap;
	word-wrap: break-word;

	.error {
		color: #ff4444;
		font-weight: bold;
	}

	.success {
		color: ${colorGreen};
		font-weight: bold;
	}

	.warning {
		color: ${colorYellow};
		font-weight: bold;
	}

	.info {
		color: ${colorBlue};
		font-weight: bold;
	}

	.command {
		color: ${colorViolet};
		font-weight: bold;
	}

	.function {
		color: ${colorBlue};
		font-weight: bold;
	}

	.device {
		color: ${colorYellow};
		font-weight: bold;
	}

	.raid {
		color: ${colorViolet};
		font-weight: bold;
	}

	.raid-status {
		color: ${colorBlue};
		font-weight: bold;
	}

	.serial {
		color: ${colorGreen};
		font-weight: bold;
	}

	.identifier {
		color: ${colorBlue};
		font-weight: bold;
	}

	.duration {
		color: ${colorYellow};
		font-weight: bold;
	}

	.timestamp {
		color: ${colorGray};
		font-weight: bold;
	}

	.json {
		color: ${colorGreen};
		font-weight: bold;
	}

	.array {
		color: ${colorYellow};
		font-weight: bold;
	}

	.string {
		color: ${colorViolet};
		font-weight: bold;
	}

	.size {
		color: ${colorGreen};
		font-weight: bold;
	}

	.status {
		color: ${colorGray};
		font-weight: bold;
	}

	.system-command {
		color: ${colorViolet};
		font-weight: bold;
	}

	.model {
		color: ${colorYellow};
		font-weight: bold;
	}

	.uuid {
		color: ${colorViolet};
		font-weight: bold;
	}

	/* Custom scrollbar */
	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: ${colorBackgroundBody};
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb {
		background: ${colorBorder};
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: ${colorHover};
	}
`;

const EmptyIndicator = styled.div`
	text-align: center;
	padding: 16px;
	color: ${colorText};
	font-size: 0.9rem;
	font-style: italic;
	border-top: 1px solid ${colorBorder};
`;

const HeaderContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 16px;
	padding: 16px;
	background-color: ${colorBackground};
	border-bottom: 1px solid ${colorBorder};
`;

const HeaderTop = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	font-size: 2rem;
	color: ${colorText};
	cursor: pointer;
	padding: 0;
	line-height: 1;
`;

const CloseIcon = styled.span`
	font-size: 1.5rem;
`;

const ModalTitle = styled.h4`
	color: ${colorText};
	font-weight: 600;
	margin: 0;
`;

const HeaderInfo = styled.div`
	display: flex;
	justify-content: space-around;
	gap: 24px;
	padding: 16px;
	background-color: ${colorBackgroundBody};
	border-radius: 8px;
	border: 1px solid ${colorBorder};
`;

const InfoCard = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 8px 16px;
	border-radius: 8px;
`;

const InfoIcon = styled.span`
	font-size: 1.2rem;
	color: ${colorAccent};
`;

const InfoContent = styled.div`
	display: flex;
	flex-direction: column;
`;

const InfoLabel = styled.span`
	font-size: 0.8rem;
	color: ${colorText};
	opacity: 0.7;
`;

const InfoValue = styled.span`
	font-weight: 600;
	color: ${colorText};
`;
